import {
  PRICING_PLAN_NAME_BY_TIER,
  pricingPlansModalMessages,
} from '@/features/settings/billing/components/pricing-plans-modal/pricing-plans-modal.messages';
import { PlanPeriodTabs, PlanPriceDisplay, type BillingPeriod } from '@heidi/commerce';
import { Period } from '@heidi/commerce/contracts';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { memo, useState, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  PRICING_PLANS_MODAL_CARD_CLASSES,
  PricingPlanCardBase,
  type PricingPlanCardFeature,
} from './pricing-plan-card-base';
import { PricingPlanCardCtaButton } from './pricing-plan-card-cta-button';
import { usePlanPriceDisplay } from './use-plan-price-display';

/** The modal renders this card for the two self-serve Clinician tiers only. */
type PricingPlanCardTier =
  | SubscriptionTier.CLINICIAN_PRO
  | SubscriptionTier.CLINICIAN_PRO_PLUS;

export interface PricingPlanCardProps {
  tier: PricingPlanCardTier;
  /** Seat the card prices and sells. Practice + MAX renders as Practice Max. Defaults to Standard. */
  seatType?: BillingSeatType;
  /** Standard/Max control for the Practice column; takes the row the period tabs use on Clinician. */
  seatToggle?: ReactNode;
  isHighlighted?: boolean;
  planSubtitle?: ReactNode;
  features: PricingPlanCardFeature[];
  featuresSectionTitle?: ReactNode;
  ctaText: ReactNode;
  /** Invoked with the card's selected billing period. When undefined, the CTA button is not rendered (e.g. Apple-compliant builds). */
  onCtaClick?: (period: Period) => void;
  isCtaLoading?: boolean;
  /** When true, CTA button is disabled (e.g. team is not Stripe managed). */
  isCtaDisabled?: boolean;
  /** Tooltip shown when CTA is disabled (e.g. "Contact Sales to upgrade"). */
  ctaDisabledTooltip?: string;
  footer?: ReactNode;
}

function PricingPlanCardComponent({
  tier,
  seatType = BillingSeatType.STANDARD,
  seatToggle,
  isHighlighted,
  planSubtitle,
  features,
  featuresSectionTitle,
  ctaText,
  onCtaClick,
  isCtaLoading,
  isCtaDisabled,
  ctaDisabledTooltip,
  footer,
}: PricingPlanCardProps) {
  const isProPlus = tier === SubscriptionTier.CLINICIAN_PRO_PLUS;
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annual');
  const selectedPeriod =
    isProPlus || billingPeriod !== 'monthly' ? Period.ONE_YEAR : Period.ONE_MONTH;
  const isPracticeMax = isProPlus && seatType === BillingSeatType.MAX;
  const { price, locale, copy } = usePlanPriceDisplay({
    tier,
    period: selectedPeriod,
    seatType,
  });

  return (
    <PricingPlanCardBase
      className={PRICING_PLANS_MODAL_CARD_CLASSES}
      isHighlighted={isHighlighted}
      isClinicianProPlusTier={isProPlus}
      planTitle={
        <FormattedMessage
          {...(isPracticeMax
            ? pricingPlansModalMessages.planNamePracticeMax
            : PRICING_PLAN_NAME_BY_TIER[tier])}
        />
      }
      planSubtitle={planSubtitle}
      periodToggle={
        isProPlus ? (
          // Practice is annual-only, so its tab row carries the seat toggle
          // instead, or stays empty to keep titles lined up.
          (seatToggle ?? <div className="min-h-6" />)
        ) : (
          <PlanPeriodTabs
            value={billingPeriod}
            onValueChange={setBillingPeriod}
            saveBadgePlacement="start"
            size="sm"
            variant={isHighlighted ? 'proTier' : 'default'}
          />
        )
      }
      priceSlot={<PlanPriceDisplay price={price} locale={locale} copy={copy} />}
      cta={
        onCtaClick ? (
          <PricingPlanCardCtaButton
            variant={isProPlus ? 'outline' : 'pro'}
            icon={
              isProPlus ? <TierPracticeFilled size={16} /> : <TierProFilled size={16} />
            }
            onClick={() => onCtaClick(selectedPeriod)}
            isCtaLoading={isCtaLoading}
            isCtaDisabled={isCtaDisabled}
            ctaText={ctaText}
          />
        ) : null
      }
      isCtaDisabled={isCtaDisabled}
      ctaDisabledTooltip={ctaDisabledTooltip}
      features={features}
      featuresSectionTitle={featuresSectionTitle}
      footer={footer}
    />
  );
}

export const PricingPlanCard = memo(PricingPlanCardComponent);
